import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ChevronRight, Cake, Sparkles, Gift, Briefcase, Baby } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { TrustStrip } from "../components/TrustStrip";
import { ProductCard } from "../components/ProductCard";
import { ProductModal } from "../components/ProductModal";

export default function Home() {
    const [products, setProducts] = useState([]);
    const [active, setActive] = useState(null);

    useEffect(() => {
        fetchProducts().then(setProducts).catch(console.error);
    }, []);

    const bestsellers = products.filter((p) => p.category === "bestseller");

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 -z-10"
                    style={{
                        background:
                            "linear-gradient(135deg, #FFE5EC 0%, #FFF5F7 50%, #FFDEEA 100%)",
                    }}
                />
                <div className="hearts-bg absolute inset-0 -z-10 opacity-70" />

                <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-32 md:pt-24 md:pb-40 grid md:grid-cols-2 gap-10 items-center">
                    <div className="fade-up">
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--sss-pink)] font-semibold">
                            <Sparkles size={14} /> Boutique Dessert Studio
                        </span>
                        <h1 className="mt-4 font-serif-display text-5xl md:text-7xl font-bold leading-[1.05]">
                            Sweet moments.<br />
                            <span className="italic" style={{ color: "var(--sss-pink)" }}>Made for you.</span>
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-[var(--sss-text-muted)] max-w-md leading-relaxed">
                            Indulge in our handcrafted treats made with love and the finest ingredients —
                            chocolate-covered strawberries, cheesecake cups, candied fruit and more.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/menu" className="btn-pink" data-testid="hero-shop-now">
                                Shop Now <Heart size={14} fill="#fff" strokeWidth={0} />
                            </Link>
                            <Link to="/custom-orders" className="btn-outline-pink" data-testid="hero-custom-orders">
                                Custom Orders <ChevronRight size={16} />
                            </Link>
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full opacity-60" style={{ background: "radial-gradient(circle, #FFB6C1 0%, transparent 70%)" }} />
                        <div className="relative aspect-square rounded-[2rem] overflow-hidden sss-shadow-lg rotate-2">
                            <img
                                src="/products/chocolate-strawberries.jpg"
                                alt="Sugar Sweet Society treats"
                                className="w-full h-full object-cover"
                            />
                            <div
                                className="absolute bottom-5 right-5 bg-white rounded-full px-5 py-3 sss-shadow flex items-center gap-2"
                                aria-hidden="true"
                            >
                                <Heart size={16} fill="#FF2A7A" strokeWidth={0} />
                                <span className="text-sm font-semibold">Baked fresh daily</span>
                            </div>
                        </div>
                        <div className="absolute -bottom-8 -left-8 w-44 hidden md:block rounded-2xl overflow-hidden sss-shadow rotate-[-4deg]">
                            <img
                                src="/products/cheesecake-cups.jpg"
                            alt="Strawberry cheesecake cup"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <TrustStrip />

            {/* BESTSELLERS */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="bestsellers-section">
                <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-[var(--sss-pink)] font-semibold">Most Loved</p>
                    <h2 className="font-serif-display text-4xl md:text-5xl font-bold mt-2">Our Bestsellers</h2>
                    <Heart size={16} className="inline mt-3 text-[var(--sss-pink)]" fill="#FF2A7A" strokeWidth={0} />
                </div>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {bestsellers.map((p) => (
                        <ProductCard key={p.id} product={p} onOpen={setActive} />
                    ))}
                </div>

                <div className="text-center mt-10">
                    <Link to="/menu" className="btn-outline-pink" data-testid="view-full-menu">
                        View Full Menu <ChevronRight size={16} />
                    </Link>
                </div>
            </section>

            {/* CUSTOM ORDERS BANNER */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 mt-28" data-testid="custom-orders-banner">
                <div className="rounded-[2.5rem] overflow-hidden grid md:grid-cols-2 sss-shadow bg-white">
                    <div className="p-8 md:p-12">
                        <p className="text-xs uppercase tracking-[0.3em] text-[var(--sss-pink)] font-semibold">For Every Occasion</p>
                        <h2 className="font-serif-display text-3xl md:text-5xl font-bold mt-3 leading-tight">
                            Custom Orders<br />for Any Occasion
                        </h2>
                        <p className="mt-4 text-[var(--sss-text-muted)] max-w-md">
                            We create beautiful, custom treats for events, parties, corporate gifts and more.
                        </p>
                        <p className="mt-4 font-serif-display italic text-[var(--sss-pink)]">
                            Minimum order starts at $150
                        </p>

                        <div className="mt-6 grid grid-cols-3 sm:grid-cols-5 gap-3 text-center">
                            {[
                                { Icon: Cake, label: "Birthdays" },
                                { Icon: Heart, label: "Weddings" },
                                { Icon: Baby, label: "Baby Showers" },
                                { Icon: Briefcase, label: "Corporate" },
                                { Icon: Gift, label: "Holidays" },
                            ].map(({ Icon, label }) => (
                                <div key={label} className="flex flex-col items-center gap-1.5">
                                    <div className="w-11 h-11 rounded-full bg-[var(--sss-blush)] flex items-center justify-center">
                                        <Icon size={18} className="text-[var(--sss-pink)]" strokeWidth={1.5} />
                                    </div>
                                    <span className="text-[11px] text-[var(--sss-text-muted)] leading-tight">{label}</span>
                                </div>
                            ))}
                        </div>

                        <Link to="/custom-orders" className="btn-pink mt-8" data-testid="custom-order-cta">
                            Request a Custom Order <Heart size={14} fill="#fff" strokeWidth={0} />
                        </Link>
                    </div>
                    <div className="relative min-h-[300px] md:min-h-[420px]">
                        <img
                            src="/products/treats-collage.jpg"
                            alt="Collage of Sugar Sweet Society treats"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {active && <ProductModal product={active} onClose={() => setActive(null)} />}
        </div>
    );
}
